// ============================================
// SERVICE EMAIL - EmailJS
// ============================================

import emailjs from '@emailjs/browser';

// Configuration EmailJS
const EMAILJS_CONFIG = {
  serviceId: 'service_yv02dnf',
  templateClient: 'template_a2rips3',   // Order Confirmation
  templateAdmin: 'template_b4wys79',    // Admin Notification
  publicKey: 'NDZ3UQiorhMdJfkEb'
};

// Infos du centre (pour le template client)
const CENTRE_INFO = {
  adresse: '53 la Charrière du Commerce, 50230 Agon-Coutainville',
  telephone: '02 44 84 99 96',
  email: 'laserbayagon@gmail.com',
  site: 'www.laserbay.net'
};

// Initialiser EmailJS
emailjs.init(EMAILJS_CONFIG.publicKey);

// Formater la date en français
function formatDateFR(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('fr-FR', { 
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
  });
}

// Formater le mode de paiement
function formatPaymentMethod(method) {
  switch (method) {
    case 'stripe': return 'Carte bancaire (en ligne, intégral)';
    case 'stripe_deposit': return 'Acompte CB en ligne + solde sur place';
    case 'on_site': return 'Sur place (espèces ou CB)';
    case 'bank_transfer': return 'Virement bancaire';
    default: return method || 'Non précisé';
  }
}

// Formater un montant en euros (gère les décimales propres)
function formatEuros(amount) {
  if (amount === null || amount === undefined) return '0€';
  const num = Number(amount);
  // Si entier, pas de décimales. Sinon 2 décimales.
  return Number.isInteger(num) ? `${num}€` : `${num.toFixed(2)}€`;
}

// Construire le bloc HTML acompte/solde à injecter dans options_html
// Renvoie une chaîne HTML compatible avec le template (style aligné)
function buildDepositHtmlBlock(bookingData) {
  // Le bloc n'est généré QUE si on est en mode acompte (stripe_deposit)
  // OU si on a clairement un acompte versé sur la résa
  const isDepositMode = bookingData.paymentMethod === 'stripe_deposit';
  const hasDepositInfo = bookingData.depositRequired || bookingData.depositAmount > 0;
  
  if (!isDepositMode && !hasDepositInfo) return '';
  
  const deposit = bookingData.depositAmount || 0;
  const remaining = bookingData.remainingAmount !== undefined 
    ? bookingData.remainingAmount 
    : Math.max(0, (bookingData.totalPrice || 0) - deposit);
  
  // Style aligné avec .price-line du template HTML
  const lineStyle = 'display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e2e8f0; font-size: 14px;';
  const labelStyle = 'color: #475569;';
  const valueStyle = 'color: #1e293b; font-weight: 600;';
  
  return `
    <div style="${lineStyle}">
      <span style="${labelStyle}">🔒 Acompte versé en ligne</span>
      <span style="${valueStyle}; color: #10b981;">${formatEuros(deposit)} ✓</span>
    </div>
    <div style="${lineStyle}">
      <span style="${labelStyle}">💶 Solde à régler sur place</span>
      <span style="${valueStyle}; color: #d97706;">${formatEuros(remaining)}</span>
    </div>
  `;
}

// Générer un numéro de confirmation
function generateConfirmationNumber(bookingId) {
  return 'LB-' + (bookingId ? bookingId.substring(0, 8).toUpperCase() : 'XXXXX');
}

// ============================================
// 1. Email de confirmation au client
// ============================================
// Variables du template HTML :
//   nom_participant, numero_confirmation, lien_reservation,
//   date_session, heure_session, nombre_joueurs, telephone_participant,
//   adresse_centre, telephone_centre, email_centre, site_centre,
//   prix_formule_label, prix_formule_montant, options_html (HTML brut),
//   prix_total, mode_paiement, annee_actuelle
// ============================================
export async function sendConfirmationEmail(bookingData) {
  if (!bookingData.contactEmail) {
    console.log("Pas d'email client, confirmation non envoyée");
    return false;
  }

  try {
    const confirmNum = generateConfirmationNumber(bookingData.id);
    
    // Construction du HTML pour les options + bloc acompte
    // On combine ce qui existait déjà (options) + nouveau bloc acompte
    let optionsHtml = '';
    
    // 1. Lignes options additionnelles (si présentes)
    if (bookingData.selectedOptions && bookingData.selectedOptions.length > 0) {
      const lineStyle = 'display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e2e8f0; font-size: 14px;';
      const labelStyle = 'color: #475569;';
      const valueStyle = 'color: #1e293b; font-weight: 600;';
      bookingData.selectedOptions.forEach(opt => {
        const qtyLabel = (opt.quantity && opt.quantity > 1) ? ` × ${opt.quantity}` : '';
        optionsHtml += `
          <div style="${lineStyle}">
            <span style="${labelStyle}">➕ ${opt.name}${qtyLabel}</span>
            <span style="${valueStyle}">${formatEuros(opt.subtotal)}</span>
          </div>
        `;
      });
    }
    
    // 2. Bloc acompte/solde (si applicable)
    optionsHtml += buildDepositHtmlBlock(bookingData);
    
    // Mode de paiement enrichi pour le client
    let modePaiementText = formatPaymentMethod(bookingData.paymentMethod);
    if (bookingData.paymentMethod === 'stripe_deposit' && bookingData.depositAmount) {
      modePaiementText = `🔒 Acompte de ${formatEuros(bookingData.depositAmount)} déjà réglé en ligne — solde de ${formatEuros(bookingData.remainingAmount)} à régler sur place le jour J`;
    } else if (bookingData.paymentMethod === 'stripe' && bookingData.isPaid) {
      modePaiementText = `✅ Réservation intégralement payée en ligne (${formatEuros(bookingData.totalPrice)})`;
    }
    
    // Lien vers la page de gestion de la réservation (statut + futures fonctions)
    const baseUrl = (typeof window !== 'undefined' && window.location && window.location.origin) 
      ? window.location.origin 
      : 'https://laserbay.net';
    const lienReservation = `${baseUrl}/booking/status/${bookingData.id || ''}`;
    
    // Label de la formule (ligne principale du récap prix)
    const nbPlayers = bookingData.nbParticipants || 1;
    const isPerPerson = bookingData.basePrice && bookingData.basePrice !== bookingData.totalPrice;
    const prixFormuleLabel = bookingData.formulaName || 'Formule';
    // Montant de la formule = basePrice si présent (avant options), sinon totalPrice
    const formulaAmount = bookingData.basePrice || bookingData.totalPrice;
    const prixFormuleMontant = isPerPerson 
      ? `${formatEuros(formulaAmount)} (${nbPlayers} pers.)` 
      : formatEuros(formulaAmount);
    
    const templateParams = {
      // Destinataire
      to_email: bookingData.contactEmail,
      
      // Identité
      nom_participant: bookingData.contactName,
      numero_confirmation: confirmNum,
      
      // Lien vers gestion de réservation
      lien_reservation: lienReservation,
      
      // Détails session
      date_session: formatDateFR(bookingData.date),
      heure_session: bookingData.startTime,
      nombre_joueurs: bookingData.nbParticipants,
      telephone_participant: bookingData.contactPhone || 'Non renseigné',
      
      // Infos du centre
      adresse_centre: CENTRE_INFO.adresse,
      telephone_centre: CENTRE_INFO.telephone,
      email_centre: CENTRE_INFO.email,
      site_centre: CENTRE_INFO.site,
      
      // Détail prix (variables réellement utilisées par le template)
      prix_formule_label: prixFormuleLabel,
      prix_formule_montant: prixFormuleMontant,
      options_html: optionsHtml,                    // HTML brut, inclut options + bloc acompte
      prix_total: bookingData.totalPrice,
      mode_paiement: modePaiementText,
      
      // Conservé pour compatibilité ascendante (si l'ancien template est encore actif)
      prix_unitaire: `${bookingData.formulaName} — ${formatEuros(bookingData.totalPrice)} (${formatPaymentMethod(bookingData.paymentMethod)})`,
      
      // Année (footer)
      annee_actuelle: new Date().getFullYear().toString()
    };

    await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templateClient,
      templateParams
    );

    console.log('Email de confirmation envoyé à', bookingData.contactEmail);
    return true;
  } catch (error) {
    console.error('Erreur envoi email confirmation:', error);
    return false;
  }
}

// ============================================
// 2. Notification email à l'admin
// ============================================
// Variables du template : numero_confirmation, participant_name,
// participant_phone, participant_email, date_session, heure_session,
// nombre_joueurs, prix_total, statut_paiement, type_reservation,
// raison_blocage, timestamp, capacite_restante
// ============================================
export async function sendAdminNotification(bookingData) {
  try {
    const confirmNum = generateConfirmationNumber(bookingData.id);
    const now = new Date();
    const timestamp = now.toLocaleDateString('fr-FR', { 
      day: 'numeric', month: 'long', year: 'numeric', 
      hour: '2-digit', minute: '2-digit' 
    });

    // Statut de paiement détaillé pour l'admin
    let statutPaiement;
    if (bookingData.isPaid) {
      // Réservation intégralement payée
      statutPaiement = `✅ Payée intégralement (${formatPaymentMethod(bookingData.paymentMethod)})`;
    } else if (bookingData.paymentMethod === 'stripe_deposit' && bookingData.depositAmount) {
      // Acompte versé, solde à venir
      statutPaiement = `🔒 Acompte ${formatEuros(bookingData.depositAmount)} en cours de versement — solde ${formatEuros(bookingData.remainingAmount)} à encaisser sur place`;
    } else if (bookingData.paymentMethod === 'stripe') {
      // Paiement intégral en attente
      statutPaiement = `⏳ Paiement intégral en cours (${formatEuros(bookingData.totalPrice)})`;
    } else {
      // Modes traditionnels
      statutPaiement = `⏳ En attente — ${formatPaymentMethod(bookingData.paymentMethod)}`;
    }

    const templateParams = {
      // Numéro confirmation
      numero_confirmation: confirmNum,
      
      // Participant
      participant_name: bookingData.contactName,
      participant_phone: bookingData.contactPhone || 'Non renseigné',
      participant_email: bookingData.contactEmail || 'Non renseigné',
      
      // Session
      date_session: formatDateFR(bookingData.date),
      heure_session: bookingData.startTime + (bookingData.slots ? ` (${bookingData.slots.length} créneaux)` : ''),
      nombre_joueurs: bookingData.nbParticipants,
      prix_total: formatEuros(bookingData.totalPrice),
      
      // Paiement et type
      statut_paiement: statutPaiement,
      type_reservation: bookingData.formulaName + ' (' + (bookingData.category || 'individuel') + ')',
      raison_blocage: bookingData.notes || 'Aucune note',
      
      // Système
      timestamp: timestamp,
      capacite_restante: `${bookingData.nbParticipants}/10 joueurs sur ce créneau`
    };

    await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templateAdmin,
      templateParams
    );

    console.log('Notification admin envoyée');
    return true;
  } catch (error) {
    console.error('Erreur envoi notification admin:', error);
    return false;
  }
}

// ============================================
// 3. Envoyer les deux emails après une réservation
// ============================================
// ============================================
// Envoi groupé des emails de réservation
// ============================================
// IMPORTANT : on n'envoie RIEN tant que la réservation n'est pas
// confirmée. Une réservation en "pending_payment" (client parti sur
// Stripe) ne doit pas générer de mail de confirmation : s'il abandonne,
// le client aurait un mail confirmant une session inexistante.
//
// Les emails partent donc :
//   - immédiatement pour les réservations créées par l'admin
//   - après retour du paiement Stripe pour les réservations en ligne
// ============================================
export async function sendBookingEmails(bookingData) {
  // Réservation encore en attente de paiement → on ne prévient personne
  if (bookingData.status === 'pending_payment') {
    console.log('Réservation en attente de paiement : emails différés');
    return { clientSent: false, adminSent: false, skipped: true };
  }

  const results = await Promise.allSettled([
    sendConfirmationEmail(bookingData),
    sendAdminNotification(bookingData)
  ]);
  
  return {
    clientSent: results[0].status === 'fulfilled' && results[0].value === true,
    adminSent: results[1].status === 'fulfilled' && results[1].value === true
  };
}

// ============================================
// 4. Email carte cadeau (envoi au bénéficiaire)
// ============================================
// Cette fonction est appelée depuis GiftCard.js après paiement Stripe
// pour notifier le bénéficiaire qu'une carte cadeau lui a été offerte.
//
// On réutilise le template client existant (templateClient) en adaptant
// les champs pour correspondre au contexte "carte cadeau".
// Si tu veux un template dédié plus tard, il suffira de créer un nouveau
// template dans EmailJS et de remplacer EMAILJS_CONFIG.templateClient ici.
// ============================================
export async function sendGiftCardEmail(giftCardData) {
  if (!giftCardData.recipientEmail) {
    console.log("Pas d'email bénéficiaire, email carte cadeau non envoyé");
    return false;
  }

  try {
    // Description de la carte cadeau (formule ou montant libre)
    const giftDescription = giftCardData.type === 'formula' && giftCardData.formulaName
      ? `Formule "${giftCardData.formulaName}"`
      : `Montant libre de ${formatEuros(giftCardData.amount)}`;
    
    // Date de validité formatée
    const validUntilFormatted = giftCardData.validUntil 
      ? formatDateFR(giftCardData.validUntil)
      : 'un an à compter de l\'émission';
    
    // Lien vers la carte cadeau pour la consulter
    const baseUrl = (typeof window !== 'undefined' && window.location && window.location.origin) 
      ? window.location.origin 
      : 'https://laserbay.net';
    const lienCarte = `${baseUrl}/giftcard/${giftCardData.code}`;
    
    // Construction d'un options_html spécifique aux cartes cadeaux
    // (réutilise les styles du template client)
    const lineStyle = 'display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e2e8f0; font-size: 14px;';
    const labelStyle = 'color: #475569;';
    const valueStyle = 'color: #1e293b; font-weight: 600;';
    
    let optionsHtml = `
      <div style="${lineStyle}">
        <span style="${labelStyle}">🎁 Code de la carte</span>
        <span style="${valueStyle}; font-family: monospace; font-size: 16px; color: #d97706;">${giftCardData.code}</span>
      </div>
      <div style="${lineStyle}">
        <span style="${labelStyle}">📅 Valable jusqu'au</span>
        <span style="${valueStyle}">${validUntilFormatted}</span>
      </div>
    `;
    
    if (giftCardData.message) {
      optionsHtml += `
        <div style="margin-top: 12px; padding: 12px; background: #fef3c7; border-radius: 8px; font-style: italic; color: #92400e; font-size: 14px;">
          💌 "${giftCardData.message}"
        </div>
      `;
    }
    
    const templateParams = {
      // Destinataire = le bénéficiaire
      to_email: giftCardData.recipientEmail,
      
      // Identité
      nom_participant: giftCardData.recipientName || 'Heureux bénéficiaire',
      numero_confirmation: giftCardData.code,
      
      // Lien vers la carte cadeau
      lien_reservation: lienCarte,
      
      // On réutilise les champs existants avec un détournement sémantique
      date_session: 'À choisir lors de votre réservation',
      heure_session: 'À votre convenance',
      nombre_joueurs: '—',
      telephone_participant: giftCardData.senderName 
        ? `Offert par ${giftCardData.senderName}` 
        : 'Cadeau anonyme',
      
      // Infos du centre
      adresse_centre: CENTRE_INFO.adresse,
      telephone_centre: CENTRE_INFO.telephone,
      email_centre: CENTRE_INFO.email,
      site_centre: CENTRE_INFO.site,
      
      // Détail de la carte cadeau
      prix_formule_label: `🎁 ${giftDescription}`,
      prix_formule_montant: formatEuros(giftCardData.amount),
      options_html: optionsHtml,
      prix_total: giftCardData.amount,
      mode_paiement: '💳 Carte cadeau pré-payée — il ne vous reste qu\'à réserver !',
      
      // Compatibilité ascendante
      prix_unitaire: `${giftDescription} — ${formatEuros(giftCardData.amount)}`,
      
      // Footer
      annee_actuelle: new Date().getFullYear().toString()
    };
    
    await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templateClient,
      templateParams
    );
    
    console.log('Email carte cadeau envoyé à', giftCardData.recipientEmail);
    return true;
  } catch (error) {
    console.error('Erreur envoi email carte cadeau:', error);
    return false;
  }
}
